(function () {
  'use strict';

  var currentScript = document.currentScript;
  var CHANNEL_PLUGIN_KEY =
    (currentScript && currentScript.getAttribute('data-channel-plugin-key')) || '';

  var channelBooted = false;
  var channelLoading = false;
  var channelReadyQueue = [];

  // Channel.io: install queue stub before the CDN script loads.
  function initChannelIOStub() {
    var w = window;

    if (w.ChannelIO) {
      return;
    }

    var ch = function () {
      ch.c(arguments);
    };

    ch.q = [];
    ch.c = function (args) {
      ch.q.push(args);
    };
    w.ChannelIO = ch;
  }

  // Channel.io: inject CDN script on demand (first chat interaction).
  function loadChannelIOScript(onReady) {
    var w = window;

    if (w.ChannelIOInitialized) {
      if (onReady) {
        onReady();
      }
      return;
    }

    w.ChannelIOInitialized = true;
    var script = document.createElement('script');
    script.type = 'text/javascript';
    script.async = true;
    script.src = 'https://cdn.channel.io/plugin/ch-plugin-web.js';
    script.onload = function () {
      if (onReady) {
        onReady();
      }
    };
    var firstScript = document.getElementsByTagName('script')[0];

    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    }
  }

  // Channel.io: boot once after SDK loads; custom launcher handled in theme markup.
  function bootChannelIO() {
    if (channelBooted || !CHANNEL_PLUGIN_KEY || !window.ChannelIO) {
      return;
    }

    window.ChannelIO('boot', {
      pluginKey: CHANNEL_PLUGIN_KEY,
      hideChannelButtonOnBoot: true
    });
    channelBooted = true;
  }

  // Channel.io: load SDK and boot before running messenger callbacks.
  function ensureChannelReady(callback) {
    if (channelBooted) {
      callback();
      return;
    }

    channelReadyQueue.push(callback);

    if (channelLoading) {
      return;
    }

    channelLoading = true;
    loadChannelIOScript(function () {
      bootChannelIO();
      channelLoading = false;
      var queue = channelReadyQueue.slice();
      channelReadyQueue = [];
      queue.forEach(function (fn) {
        fn();
      });
    });
  }

  // Channel launcher: lazy-load SDK and open messenger from button or #showMessenger links.
  function bindChannelLauncher() {
    function openMessenger(event) {
      if (event) {
        event.preventDefault();
      }

      ensureChannelReady(function () {
        window.ChannelIO('showMessenger');
      });
    }

    var launcher = document.querySelector('[data-channel-launcher]');
    var button = launcher && launcher.querySelector('.metamall-channel-launcher__button');

    if (button) {
      button.addEventListener('click', openMessenger);
    }

    document.querySelectorAll('a[href="#showMessenger"]').forEach(function (link) {
      link.addEventListener('click', openMessenger);
    });
  }

  // Cart drawer: toggle body class when drawer open state changes.
  function initCartDrawerFloatingFix() {
    if (window.MetamallCartDrawerFloatingFixInitialized) {
      return;
    }

    window.MetamallCartDrawerFloatingFixInitialized = true;

    var observer;
    var rafId;

    function getCartDrawer() {
      return document.getElementById('CartDrawer');
    }

    function updateCartDrawerState() {
      var cartDrawer = getCartDrawer();
      var isOpen =
        cartDrawer &&
        (cartDrawer.hasAttribute('open') ||
          cartDrawer.hasAttribute('active') ||
          cartDrawer.classList.contains('active') ||
          cartDrawer.classList.contains('is-open') ||
          !cartDrawer.hasAttribute('hidden') ||
          cartDrawer.getAttribute('aria-hidden') === 'false');

      if (document.body) {
        document.body.classList.toggle('metamall-cart-drawer-open', Boolean(isOpen));
      }
    }

    function scheduleCartDrawerUpdate() {
      window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(updateCartDrawerState);
    }

    function bindCartDrawerObserver() {
      var cartDrawer = getCartDrawer();

      scheduleCartDrawerUpdate();

      if (!cartDrawer || !window.MutationObserver) {
        return;
      }

      if (observer) {
        observer.disconnect();
      }

      observer = new MutationObserver(scheduleCartDrawerUpdate);
      observer.observe(cartDrawer, {
        attributes: true,
        attributeFilter: ['open', 'active', 'hidden', 'aria-hidden', 'class']
      });
    }

    bindCartDrawerObserver();
    document.addEventListener('shopify:section:load', bindCartDrawerObserver);
  }

  // Floating UI: reserve bottom inset using theme CSS vars and sticky bar visibility.
  function initFloatingReservedBottom() {
    var root = document.documentElement;
    var barObserver;
    var resizeObserver;
    var rafId;

    function readCssPx(customProperty) {
      var value = parseFloat(window.getComputedStyle(root).getPropertyValue(customProperty));
      return Number.isFinite(value) ? value : 0;
    }

    function isVisible(element) {
      var style = window.getComputedStyle(element);

      return (
        style.display !== 'none' &&
        style.visibility !== 'hidden' &&
        Number(style.opacity) !== 0 &&
        element.getClientRects().length > 0
      );
    }

    function updateReservedBottom() {
      var reservedBottom = 0;
      var body = document.body;

      if (body && body.classList.contains('mobile-sticky-bar-up')) {
        reservedBottom = Math.max(reservedBottom, readCssPx('--mobile-sticky-bar-height'));
      }

      document.querySelectorAll('sticky-atc-bar, .sticky-atc-bar').forEach(function (bar) {
        if (!bar.classList.contains('sticky-atc-bar--show') || !isVisible(bar)) {
          return;
        }

        var barHeight = readCssPx('--sticky-atc-bar-height') || bar.offsetHeight;
        reservedBottom = Math.max(reservedBottom, barHeight);
      });

      root.style.setProperty('--metamall-floating-reserved-bottom', Math.ceil(reservedBottom) + 'px');
    }

    function scheduleUpdate() {
      window.cancelAnimationFrame(rafId);
      rafId = window.requestAnimationFrame(updateReservedBottom);
    }

    function bindBarObservers() {
      if (barObserver) {
        barObserver.disconnect();
      }

      if (resizeObserver) {
        resizeObserver.disconnect();
      }

      if (!document.body) {
        return;
      }

      if (window.MutationObserver) {
        barObserver = new MutationObserver(scheduleUpdate);
        barObserver.observe(document.body, {
          attributes: true,
          attributeFilter: ['class']
        });

        document.querySelectorAll('sticky-atc-bar, .sticky-atc-bar, mobile-sticky-bar, .mobile-sticky-bar').forEach(
          function (bar) {
            barObserver.observe(bar, {
              attributes: true,
              attributeFilter: ['class', 'hidden', 'open', 'aria-hidden']
            });
          }
        );
      }

      if (window.ResizeObserver) {
        resizeObserver = new ResizeObserver(scheduleUpdate);
        document.querySelectorAll('sticky-atc-bar, .sticky-atc-bar, mobile-sticky-bar, .mobile-sticky-bar').forEach(
          function (bar) {
            resizeObserver.observe(bar);
          }
        );
      }
    }

    window.addEventListener('load', scheduleUpdate);
    window.addEventListener('resize', scheduleUpdate);
    document.addEventListener('shopify:section:load', function () {
      bindBarObservers();
      scheduleUpdate();
    });

    bindBarObservers();
    scheduleUpdate();
  }

  // Office chair blog: correct CTA links on cached article HTML.
  function fixOfficeChairBlogLinks() {
    var articlePath = decodeURIComponent(window.location.pathname || '');

    if (
      articlePath.indexOf('/blogs/ニュース/') !== 0 ||
      articlePath.indexOf('オフィスチェア') === -1
    ) {
      return;
    }

    var root = document.querySelector('.mm-office-chair-blog');

    if (!root) {
      return;
    }

    function setProductLinks(sectionId, primaryHref, secondaryHref) {
      var section = document.getElementById(sectionId);

      if (!section) {
        return;
      }

      var primary = section.querySelector('.mm-btn--primary');
      var secondary = section.querySelector('.mm-btn--ghost');

      if (primary && primaryHref) {
        primary.setAttribute('href', primaryHref);
      }

      if (secondary && secondaryHref) {
        secondary.setAttribute('href', secondaryHref);
      }
    }

    setProductLinks(
      'bgy-m-001',
      'https://metamalljp.com/products/bgy-m-001',
      'https://metamalljp.com/pages/corporate-bulk-order-consultation'
    );
    setProductLinks(
      'lby-m-005',
      'https://metamalljp.com/products/エグゼクティブチェア-オフィスチェア-マネジメントチェア-会議用チェア-lby-005',
      'https://metamalljp.com/collections/エグゼクティブチェア'
    );
    setProductLinks(
      'bgy-m-048',
      'https://metamalljp.com/products/オフィスチェア-デスクチェア-イス-ランバーサポート-人間工学-ウレタンフォーム-メッシュ張地-ラテックス-ブラック-bgy-048',
      'https://metamalljp.com/collections/オフィスチェア'
    );
  }

  // Page init: run layout-dependent DOM work after parse via rAF.
  function runAfterLayout(callback) {
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(callback);
    });
  }

  function onDocumentReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  }

  initChannelIOStub();

  onDocumentReady(function () {
    fixOfficeChairBlogLinks();
    bindChannelLauncher();

    runAfterLayout(function () {
      initCartDrawerFloatingFix();
      initFloatingReservedBottom();
    });
  });
})();

